import { Stack, Typography } from '@mui/material'
import { JobDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import {
    JobGroupDto,
    JobGroupType,
} from '@operations/features/equipmentOperator/models/job-group.model'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useSnackbar } from 'notistack'
import { JobCard } from '../JobCard/JobCard'
import { JobCardMinimalistic } from '../MinimalisticCard/JobCardMinimalistic'

interface Props {
  jobGroups: JobGroupDto[]
  highlightText?: string
}

export const JobList = observer(({ jobGroups, highlightText }: Props) => {
  const { t } = useTranslate()

  const { appStore, equipmentOperatorUIStore, equipmentOperatorStepperUIStore, tenantConfigStore } =
    useOperationsStore()

  const { closeSnackbar } = useSnackbar()

  const handleConfirm = async (job: JobDto) => {
    appStore.forceLoader()
    closeSnackbar()
    appStore.unforceLoader()

    if (equipmentOperatorStepperUIStore.canSkipStepper(job)) {
      equipmentOperatorUIStore.hideJob(job.workInstructionId)

      appStore.showAlertForCancellableRequest(
        'success',
        `${job.cargoUnit!.displayName} ${t('isBeingConfirmed', 'is being confirmed')}`,
        async () => {
          await equipmentOperatorStepperUIStore
            .finishJobByWorkInstructionId(job.workInstructionId)
            .catch(() => equipmentOperatorUIStore.showJob(job.workInstructionId))
          equipmentOperatorUIStore.closeSearchIfFinishingLastNotification(job.workInstructionId)
        },
        undefined,
        () => {
          equipmentOperatorUIStore.showJob(job.workInstructionId)
        },
      )
    } else {
      await equipmentOperatorStepperUIStore.openStepper(job)
      await equipmentOperatorStepperUIStore.startJob(job)
    }
  }

  const getGroupDisplayName = (group: JobGroupDto) => {
    if (group.type === JobGroupType.vesselBay) return `${t('bay', 'Bay')} ${group.groupName}`

    if (group.groupName !== '') return group.groupName

    if (group.identifier === -1)
      return t('ungroupedHighPriorityContainers', 'Ungrouped high priority containers')
    else return t('ungroupedContainers', 'Ungrouped containers')
  }

  const displayGroupName = jobGroups.some(g => g.identifier > 0)

  return (
    <>
      {jobGroups.map((group, index) => (
        <Stack
          key={`${group.groupName}_${index}`}
          gap={theme => theme.customSpacing.m}
          marginBottom={theme => (displayGroupName ? theme.customSpacing.xll : 0)}
        >
          {displayGroupName && <Typography variant='h3'>{getGroupDisplayName(group)}</Typography>}

          {group.jobs.map(job =>
            equipmentOperatorUIStore.isFullView ? (
              <JobCard
                key={`${job.workInstructionId}`}
                job={job}
                isConfirm={!!equipmentOperatorStepperUIStore.canSkipStepper(job)}
                currentEquipmentType={equipmentOperatorUIStore.typeOfSelectedEquipment}
                onConfirm={handleConfirm}
                highlightText={highlightText}
                fieldsToShow={tenantConfigStore.getOperatorFieldsByEquipmentAndOperationTypeAndCarrierType(
                  equipmentOperatorUIStore.typeOfSelectedEquipment,
                  job.operationType,
                  job.carrierVisit?.type,
                )}
                skipYardPlanning={tenantConfigStore.skipYardPlanning}
              />
            ) : (
              <JobCardMinimalistic
                key={`${job.workInstructionId}`}
                job={job}
                isConfirm={!!equipmentOperatorStepperUIStore.canSkipStepper(job)}
                highlightText={highlightText}
                currentEquipment={equipmentOperatorUIStore.equipment}
                onConfirm={handleConfirm}
                skipYardPlanning={tenantConfigStore.skipYardPlanning}
              />
            ),
          )}
        </Stack>
      ))}
    </>
  )
})
